/* eslint-disable */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FormResource, FormResourcePathGenerator } from '../form/form-resource.service';
import { IconResource } from '../task/icon.service';
import { DqlResourceModel } from '../dql/dql-resource.model';
import { BaseHttpService, ResourceHelper } from '../util/http-services';
import { HttpClient } from '@angular/common/http';
import { ResourceQueryResult } from '../util/services';
import { EmptyMessage, IdentityMessage } from '../util/messages';
import { DqlStoredQueryResourceService } from '../dql/dql-stored-query-resource.service';
import { TypeBasedRoleResource } from '../user-group.service';
import { LedgerNumberResource } from '../ledger/number/ledger-number-resource.service';

/* eslint-enable */

@Injectable()
export class CustomerResourceService extends BaseHttpService {

  private static readonly baseUrl = '/customers/';

  private _dqlStoredResourceService: DqlStoredQueryResourceService;

  // <editor-fold desc="CRUD">

  query(request: CustomerResource.QueryRequest): Observable<ResourceQueryResult<CustomerResource.Customer>> {
    return this.http.get<ResourceQueryResult<CustomerResource.Customer>>(this.url, this.parseParams(request));
  }

  get(request: CustomerResource.GetRequest): Observable<CustomerResource.Customer> {
    return this.http.get<CustomerResource.Customer>(this.url + `${request.customer_id}`, this.parseParams(request));
  }

  create(request: CustomerResource.CreateRequest): Observable<CustomerResource.Customer> {
    return this.http.post<CustomerResource.Customer>(this.url, request);
  }

  update(request: CustomerResource.UpdateRequest): Observable<CustomerResource.Customer> {
    return this.http.put<CustomerResource.Customer>(this.url + `${request.customer_id}`, request);
  }

  setDisabled(request: CustomerResource.DisableRequest): Observable<CustomerResource.Customer> {
    return this.http.patch<CustomerResource.Customer>(this.url + `${request.customer_id}/disabled`, request);
  }

  getDqlModel(request: CustomerResource.DqlRequest): Observable<DqlResourceModel.QueryableModel> {
    const dqlUrl = request.customer_id ? `dql/model/${request.customer_id}` : `dql/model` + '?no_progress_bar=true';
    return this.http.get<DqlResourceModel.QueryableModel>(this.url + dqlUrl);
  }

  counts(request: EmptyMessage): Observable<CustomerResource.CustomerCount[]> {
    return this.http.get<CustomerResource.CustomerCount[]>(this.url + `counts`, this.parseParams(request));
  }

  // </editor-fold>

  // <editor-fold desc="Form">

  getForm(request: FormResource.GetFormRequest): Observable<FormResource.Form> {
    return this.http.get<FormResource.Form>(this.url + FormResourcePathGenerator.GetForm(request));
  }

  createFormGroup(request: FormResource.CreateGroupRequest): Observable<FormResource.CreateGroupResponse> {
    return this.http.post<FormResource.CreateGroupResponse>(this.url + FormResourcePathGenerator.CreateGroup(request), request);
  }

  updateFormGroup(request: FormResource.UpdateGroupRequest): Observable<FormResource.UpdateGroupResponse> {
    return this.http.put<FormResource.UpdateGroupResponse>(this.url + FormResourcePathGenerator.UpdateGroup(request), request);
  }

  disableFormGroup(request: FormResource.DisableGroupRequest): Observable<FormResource.DisableGroupResponse> {
    return this.http.patch<FormResource.DisableGroupResponse>(this.url + FormResourcePathGenerator.DisableGroup(request), request);
  }

  moveFormGroup(request: FormResource.MoveGroupRequest): Observable<FormResource.MoveGroupResponse> {
    return this.http.post<FormResource.MoveGroupResponse>(this.url + FormResourcePathGenerator.MoveGroup(request), request);
  }

  createFormField(request: FormResource.CreateFieldRequest): Observable<FormResource.CreateFieldResponse> {
    return this.http.post<FormResource.CreateFieldResponse>(this.url + FormResourcePathGenerator.CreateField(request), request);
  }

  updateFormField(request: FormResource.UpdateFieldRequest): Observable<FormResource.UpdateFieldResponse> {
    return this.http.post<FormResource.UpdateFieldResponse>(this.url + FormResourcePathGenerator.UpdateField(request), request);
  }

  disableFormField(request: FormResource.DisableFieldRequest): Observable<FormResource.DisableFieldResponse> {
    return this.http.patch<FormResource.DisableFieldResponse>(this.url + FormResourcePathGenerator.DisableField(request), request);
  }

  moveFormFieldToGroup(request: FormResource.MoveFieldToGroupRequest): Observable<FormResource.MoveFieldToGroupResponse> {
    return this.http.post<FormResource.MoveFieldToGroupResponse>(this.url + FormResourcePathGenerator.MoveFieldToGroup(request), request);
  }

  import(request: FormResource.ImportRequest): Observable<FormResource.Form> {
    return this.http.post<FormResource.Form>(this.url + FormResourcePathGenerator.Import(request), request);
  }

  export(request: FormResource.ExportRequest): Observable<FormResource.FormImportDocument> {
    return this.http.post<FormResource.FormImportDocument>(this.url + FormResourcePathGenerator.Export(request), null);
  }

  // </editor-fold>

  constructor(private http: HttpClient, resourceHelper: ResourceHelper) {
    super(resourceHelper, CustomerResourceService.baseUrl);
    this._dqlStoredResourceService = new DqlStoredQueryResourceService(http, resourceHelper, CustomerResourceService.baseUrl);
  }

  get dqlStoredResourceService(): DqlStoredQueryResourceService {
    return this._dqlStoredResourceService;
  }
}

@Injectable()
export class CustomerRoleResourceService extends BaseHttpService {

  getRoles(request: IdentityMessage): Observable<TypeBasedRoleResource[]> {
    return this.http.get<TypeBasedRoleResource[]>(this.url + `${request.id}/roles`);
  }

  createRole(request: TypeBasedRoleResource): Observable<IdentityMessage> {
    return this.http.post<IdentityMessage>(this.url + `${request.type_id}/roles`, request);
  }

  updateRole(request: TypeBasedRoleResource): Observable<EmptyMessage> {
    return this.http.put<EmptyMessage>(this.url + `${request.type_id}/roles/${request.id}`, request);
  }

  deleteRole(request: TypeBasedRoleResource): Observable<EmptyMessage> {
    return this.http.delete<EmptyMessage>(this.url + `${request.type_id}/roles/${request.id}`);
  }

  constructor(private http: HttpClient, private resourceHelper: ResourceHelper) {
    super(resourceHelper, '/customers/');
  }
}

export namespace CustomerResource {

  // <editor-fold desc="CRUD">

  export interface Customer {
    id: number;
    disabled: boolean;
    creation_time: string;
    update_time: string;
    name: string;
    complex_name: boolean;
    description: string;
    default_ledger_number?: LedgerNumberResource.LedgerNumber;
    external_id?: string,
    type: string;
    mobile_owner_type: string;
    web_owner_types?: WebOwnerType[];
    icon?: IconResource.Icon;
    owner_user_group_ids?: number[];
    use_default_managed_fields: boolean;
    managed_fields: string[];
    required_fields: string[];
    form?: FormResource.Form;
    explicit_order_number?: number;
    contact_person: boolean;
    granted_rights?: string[];
  }

  export interface QueryRequest {
    id?: string;
    disabled?: boolean;
    name?: string;
    external_id?: string,
    types?: string;
    contact_person?: boolean;
    q?: string;
    fields?: string;
    order?: string;
    page_number?: number;
    number_of_items?: number;
    no_progress_bar?: boolean;
    rights?: string;
  }

  export interface GetRequest {
    customer_id: number;
    with_form?: boolean;
    rights?: string;
  }

  export interface DqlRequest {
    customer_id?: number;
  }

  export interface CreateRequest {
    name: string;
    complex_name: boolean;
    type: string;
    mobile_owner_type: string;
    web_owner_types?: WebOwnerType[];
    description?: string;
    default_ledger_number?: number;
    external_id?: string,
    icon?: IconResource.Icon;
    owner_user_group_ids?: number[];
    use_default_managed_fields: boolean;
    managed_fields?: string[];
    required_fields: string[];
    explicit_order_number?: number;
    contact_person: boolean;
  }

  export interface UpdateRequest {
    customer_id: number;
    name: string;
    complex_name: boolean;
    type: string;
    mobile_owner_type: string;
    web_owner_types?: WebOwnerType[];
    description?: string;
    default_ledger_number?: number;
    external_id?: string,
    icon?: IconResource.Icon;
    owner_user_group_ids?: number[];
    use_default_managed_fields: boolean;
    managed_fields?: string[];
    required_fields: string[];
    explicit_order_number?: number;
    contact_person: boolean;
  }

  export interface DisableRequest {
    customer_id: number;
    disabled: boolean;
  }

  export interface CustomerCount {
    customer_id: number;
    customer_name: string;
    customer_icon?: IconResource.Icon;
    customer_record_count: number;
  }

  export interface WebOwnerType {
    owner_type: string;
    preset_user_ids?: number[];
    preset_user_group_ids?: number[];
  }

  // </editor-fold>

}
